import type { IDataService, League, Player, GameRecord, Session } from '../../shared/types';

type Listener<T> = (value: T) => void;

interface ServiceConfig {
  apiUrl:        string;
  leagueId:      string;
  delegateToken: string;
  onAuthError?:  () => void;
  onFirstLoad?:  () => void;
}

const POLL_MS = 3000;

export class KioskRestService implements IDataService {
  private readonly cfg: ServiceConfig;

  private leagueListeners:  Set<Listener<League | null>>  = new Set();
  private playersListeners: Set<Listener<Player[]>>       = new Set();
  private sessionListeners: Set<Listener<Session | null>> = new Set();
  private gamesListeners:   Set<Listener<GameRecord[]>>   = new Set();

  private lastLeague:  League | null  = null;
  private lastPlayers: Player[]       = [];
  private lastSession: Session | null = null;
  private lastGames:   GameRecord[]   = [];

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private destroyed  = false;
  private firstLoad  = false;

  constructor(cfg: ServiceConfig) {
    this.cfg = { ...cfg, apiUrl: cfg.apiUrl.replace(/\/$/, '') };
    this.startPolling();
  }

  private path(suffix: string) {
    return `${this.cfg.apiUrl}/kiosk/${this.cfg.leagueId}${suffix}`;
  }

  private headers(): Record<string, string> {
    return {
      'Content-Type':     'application/json',
      'X-Delegate-Token': this.cfg.delegateToken,
    };
  }

  private startPolling() {
    this.poll();
    this.pollTimer = setInterval(() => this.poll(), POLL_MS);
  }

  private async poll() {
    if (this.destroyed) return;
    let res: Response;
    try {
      res = await fetch(this.path('/state'), { headers: this.headers() });
    } catch {
      return;
    }
    if (res.status === 401 || res.status === 403) {
      this.cfg.onAuthError?.();
      this.destroy();
      return;
    }
    if (!res.ok) return;

    const data = await res.json() as {
      league:  League;
      session: Session | null;
      players: Player[];
      games:   GameRecord[];
    };

    if (JSON.stringify(data.league)  !== JSON.stringify(this.lastLeague))  {
      this.lastLeague = data.league;
      this.leagueListeners.forEach(cb => cb(data.league));
    }
    if (JSON.stringify(data.players) !== JSON.stringify(this.lastPlayers)) {
      this.lastPlayers = data.players;
      this.playersListeners.forEach(cb => cb(data.players));
    }
    if (JSON.stringify(data.session) !== JSON.stringify(this.lastSession)) {
      this.lastSession = data.session;
      this.sessionListeners.forEach(cb => cb(data.session));
    }
    if (JSON.stringify(data.games)   !== JSON.stringify(this.lastGames))   {
      this.lastGames = data.games ?? [];
      this.gamesListeners.forEach(cb => cb(this.lastGames));
    }

    if (!this.firstLoad) {
      this.firstLoad = true;
      this.cfg.onFirstLoad?.();
    }
  }

  onLeague(cb: Listener<League | null>): () => void {
    cb(this.lastLeague);
    this.leagueListeners.add(cb);
    return () => this.leagueListeners.delete(cb);
  }

  onPlayers(cb: Listener<Player[]>): () => void {
    cb(this.lastPlayers);
    this.playersListeners.add(cb);
    return () => this.playersListeners.delete(cb);
  }

  onGames(cb: Listener<GameRecord[]>): () => void {
    cb(this.lastGames);
    this.gamesListeners.add(cb);
    return () => this.gamesListeners.delete(cb);
  }

  onSession(cb: Listener<Session | null>): () => void {
    cb(this.lastSession);
    this.sessionListeners.add(cb);
    return () => this.sessionListeners.delete(cb);
  }

  async addPlayer(player: Player): Promise<void> {
    const res = await fetch(this.path('/players'), {
      method: 'POST', headers: this.headers(), body: JSON.stringify(player),
    });
    if (!res.ok) throw new Error(`addPlayer failed: ${res.status}`);
    await this.poll();
  }

  async setSession(session: Session | null): Promise<void> {
    const res = await fetch(this.path('/session'), {
      method: 'PUT', headers: this.headers(),
      body: session === null ? 'null' : JSON.stringify(session),
    });
    if (!res.ok) throw new Error(`setSession failed: ${res.status}`);
    await this.poll();
  }

  async addGame(game: GameRecord): Promise<void> {
    const res = await fetch(this.path('/games'), {
      method: 'POST', headers: this.headers(), body: JSON.stringify(game),
    });
    if (!res.ok) throw new Error(`addGame failed: ${res.status}`);
    await this.poll();
  }

  async setLeague():    Promise<void> {}
  async removePlayer(): Promise<void> {}
  async updateGame():   Promise<void> {}

  destroy(): void {
    this.destroyed = true;
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
